/**
 * Cleo ERP SaaS — Configuration SaaS admin (cadrage §3.5)
 *
 * Formulaire éditable : durée essai, délai grâce, email contact.
 * GET/PUT /api/admin/config/
 */
import React, { useEffect, useState } from 'react';
import {
  Card, Form, InputNumber, Input, Button, Typography,
  Spin, Alert, message, Divider, Descriptions,
} from 'antd';
import {
  SettingOutlined, SaveOutlined, LoadingOutlined,
} from '@ant-design/icons';
import api from '../../api/axios';

const { Title, Text } = Typography;

function ConfigPage() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    api.get('/api/admin/config/')
      .then(({ data }) => {
        setConfig(data);
        form.setFieldsValue(data);
      })
      .catch(() => setError('Impossible de charger la configuration.'))
      .finally(() => setLoading(false));
  }, [form]);

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const { data } = await api.put('/api/admin/config/', values);
      setConfig(data);
      form.setFieldsValue(data);
      message.success('Configuration mise à jour.');
    } catch (err) {
      message.error('Erreur lors de la sauvegarde.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 0' }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 36, color: '#10B981' }} />} />
      </div>
    );
  }

  if (error) {
    return <Alert message={error} type="error" showIcon style={{ marginTop: 16 }} />;
  }

  return (
    <div>
      <Title level={4} style={{ color: '#0F172A', marginBottom: 16 }}>
        <SettingOutlined style={{ marginRight: 8 }} />
        Configuration SaaS
      </Title>

      <Card style={{
        borderRadius: 12, border: '1px solid #E5E7EB',
        boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
        maxWidth: 640,
      }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          requiredMark={false}
        >
          <Title level={5} style={{ color: '#0F172A', marginBottom: 16 }}>
            Cycle de vie des tenants
          </Title>

          <Form.Item
            name="trial_duration_days"
            label="Durée d'essai (jours)"
            rules={[{ required: true, message: 'Requis' }]}
            help="Nombre de jours accordés aux nouveaux tenants pour tester Cleo ERP."
          >
            <InputNumber
              min={1}
              max={365}
              style={{ width: '100%' }}
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="grace_period_days"
            label="Délai de grâce avant suppression (jours)"
            rules={[{ required: true, message: 'Requis' }]}
            help="Nombre de jours après expiration avant que le tenant soit marqué pour suppression."
          >
            <InputNumber
              min={1}
              max={365}
              style={{ width: '100%' }}
              size="large"
            />
          </Form.Item>

          <Divider />

          <Title level={5} style={{ color: '#0F172A', marginBottom: 16 }}>
            Ports Docker
          </Title>

          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item
              name="min_port"
              label="Port minimum"
              rules={[{ required: true, message: 'Requis' }]}
              style={{ flex: 1 }}
            >
              <InputNumber min={8000} max={9999} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="max_port"
              label="Port maximum"
              rules={[{ required: true, message: 'Requis' }]}
              style={{ flex: 1 }}
            >
              <InputNumber min={8000} max={9999} style={{ width: '100%' }} />
            </Form.Item>
          </div>

          <Divider />

          <Title level={5} style={{ color: '#0F172A', marginBottom: 16 }}>
            Contact
          </Title>

          <Form.Item
            name="contact_email"
            label="Email de contact affiché sur le portail"
            rules={[
              { required: true, message: 'Requis' },
              { type: 'email', message: 'Format email invalide' },
            ]}
          >
            <Input size="large" />
          </Form.Item>

          <Divider />

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={saving}
              size="large"
              style={{ fontWeight: 600 }}
            >
              Enregistrer
            </Button>
          </Form.Item>
        </Form>

        {config?.updated_at && (
          <Text style={{
            color: '#94A3B8', fontSize: 12, marginTop: 16, display: 'block',
          }}>
            Dernière modification : {new Date(config.updated_at).toLocaleString('fr-FR')}
          </Text>
        )}
      </Card>
    </div>
  );
}

export default ConfigPage;
